import Image from "next/image"
import useRouterStore from "stores/routerStore"
import useT from "utils/useT"
import DragClose from "components/DragClose"
import { motion } from "framer-motion"
import router from "next/router"
import { ReactNode } from "react"
import useGlobalStore from "stores/global"
import { NextSeo } from "next-seo"
import AdBanner from "components/AdBanner"

type Props = {
  title?: string
  children?: ReactNode
  thumbnail?: string
  subtitle: string
  href: string
}

const InstantArticle = ({
  title,
  children,
  thumbnail,
  subtitle,
  href,
}: Props) => {
  const t = useT()
  const asPath = useRouterStore((state) => state.router?.asPath)
  const animationToggle = useGlobalStore((state) => state.animationToggle)

  return (
    <motion.div
      key="article"
      className="article"
      initial={animationToggle && { x: "70%", opacity: 0 }}
      animate={animationToggle && { x: [700, -25, 0], opacity: 1 }}
      exit={
        animationToggle
          ? { x: "60%", opacity: 0, transition: { duration: 0.1 } }
          : undefined
      }
      transition={
        animationToggle
          ? {
            duration: 0.3,
            type: "keyframes",
            ease: "easeOut",
          }
          : undefined
      }
    >
      <NextSeo
        title={title + " | Appollow"}
        facebook={{ appId: "381042603551124" }}
        description={subtitle}
        canonical={href}
        openGraph={{
          url: href,
          title: title + " | Appollow",
          description: subtitle,
          images: [{ url: thumbnail!, alt: title }],
          siteName: "Appollow",
          type: "article",
        }}
        twitter={{
          site: "@Appollowapp",
          cardType: "summary_large_image",
        }}
      />
      <DragClose callbackFn={() => router.back()}>
        <div className="page-content pb-0">
          <div
            className="card card-style rounded-l mb-n5"
            style={{
              height: "350px",
            }}
          >
            <Image
              src={thumbnail!}
              style={{ objectFit: "cover" }}
              alt="article thumbnail"
              priority
              fill
              unoptimized
            />
            <div className="card-bottom p-3 pb-5 mb-3">
              <h1 dir="auto" className="color-white font-600 font-25 pt-3">
                {title}
              </h1>
            </div>
            <div className="card-overlay bg-gradient" />
          </div>
          <div className="card card-style mt-n5" style={{ zIndex: 1 }}>
            <div className="card mb-0">
              <div className="content">
                <AdBanner
                  className="adsbygoogle"
                  style={{ textAlign: "center", display: "block" }}
                  data-ad-layout="in-article"
                  data-ad-format="fluid"
                  data-ad-client="ca-pub-8210764995783356"
                  data-ad-slot="4983384319"
                />
                {children}
              </div>
            </div>
            <div style={{ direction: t.dir }} className="content">
              <p dir="auto">{t.article.share}</p>
              <a
                target="_blank"
                href={`https://www.facebook.com/sharer/sharer.php?u=www.appollow.net${asPath}`}
                className="shareToFacebook icon icon-xs rounded-sm me-1 bg-facebook"
                rel="noreferrer"
              >
                <i className="fab fa-facebook-f" />
              </a>
              <a
                target="_blank"
                href={`https://twitter.com/share?text=www.appollow.net${asPath}`}
                className="shareToTwitter icon icon-xs rounded-sm me-1 bg-twitter"
                rel="noreferrer"
              >
                <i className="fab fa-twitter" />
              </a>
              <a
                target="_blank"
                href={`whatsapp://send?text=www.appollow.net${asPath}`}
                className="shareToWhatsApp icon icon-xs rounded-sm me-1 bg-whatsapp"
                rel="noreferrer"
              >
                <i className="fab fa-whatsapp" />
              </a>
              <a
                target="_blank"
                href={`https://www.linkedin.com/shareArticle?mini=true&url=www.appollow.net${asPath}`}
                className="shareToLinkedIn icon icon-xs rounded-sm me-1 bg-linkedin"
                rel="noreferrer"
              >
                <i className="fab fa-linkedin-in" />
              </a>
              <a
                target="_blank"
                href={`mailto:?body=www.appollow.net${asPath}`}
                className="shareToMail icon icon-xs rounded-sm me-1 bg-mail"
                rel="noreferrer"
              >
                <i className="fa fa-envelope" />
              </a>
            </div>
          </div>
        </div>
      </DragClose>
    </motion.div>
  )
}

export default InstantArticle
